import {
  EMA,
  SMMA,
  ATR,
  PSAR,
  AO,
  AC,
  Stochastic,
  StochasticRSI,
  RSI,
  CCI,
  ROC,
  DC,
  cRSI,
  ADX,
  ChaikinOscillator,
} from '@debut/indicators';

import { ZeroLagChaikinOscillator } from '../indicators/ZeroLagChaikin'
import { NATR } from '../indicators/NATR'

export const CandleChartSettingsDefault = {
  realtimeUpdates: false,
  onCandleUpdates: false,
  showLevels: false,
  showRecordLevels: false,
  LEVEL_HIDE_DISTANCE: 0.2, // 20% from the last close price
  ATR_LENGTH: 14,
  ATR_MAIN_LENGTH: 14,
  ATR_TEMP_LENGTH: 14,
  // 
  drawClusterExpectancy: false,
  // waves
  showZigZag: false,
  showHighLowChannels: false,
  showWaves: false,
  showWavesTrendLines: false,
  // 
  waveWindowOptimizer: {
    volatility: 3.2,
    duration: 1.4,
    minPercent: 0.005,
  },
  // waves hierarchy
  enableWaveHierarchy: false,
  showWavesHierarchy: false,
  showCorrectionBox: false,
  showInitialWavesHierarchy: false,
  showInitialWavesCascades: false,
  // indexes
  maxNestingToShowLabels: 10, // 0 = off
  showWavesHierarchyIdx: false,
  showWaveIdx: false,
  showWaveCascadeIdx: false,
  // wavesFibo
  showWaveFiboRetracement: false,
  showWaveFiboMain: false,
  showWaveFiboTemp: false,
  maxWaveFiboNesting: 3,
  // other
  showPivots: false,
}
export const CandleChartSettings = { ...CandleChartSettingsDefault }

export const BacktestSettingsDefault = {
  enableBacktest: false,
  enableLong: false,
  enableShort: false,
  showBacktestWaves: false,
  showBacktestWaveMarkers: false,
  showBacktestDebug: false,
  showExpectancyDebug: false,
  showExpectancyDebugPortfolio: false,
  showPrevExpectancy: false,
  adjustPrevExpectancy: false,
  showPullbackExpectancy: false,
  showMoveExpectancy: false,
  showBacktestTrades: false,
  showBacktestTradesDirect: false,
  showBacktestTradesInverse: false,
  // 
  takePercent: 0.5,
  takesLimit: 3,
  nextTakeDeltaPercent: 0.005,
  tradesLimit: 100,
  clusterWeightsMode: {
    value: 'equal',
    view: 'list',
    label: 'ClusterWeightsMode',
    options: [
      { text: 'Equal', value: 'equal' },
      { text: 'Expectancy', value: 'netExpectancy' },
      { text: 'Probability', value: 'prob' }
    ],
  },
  clusterStopMode: {
    value: 'overall',
    view: 'list',
    label: 'ClusterStopMode',
    options: [
      { text: 'Overall', value: 'overall' },
      { text: 'Group', value: 'group' },
      { text: 'Solo', value: 'solo' }
    ],
  },
  minExpectancy: 0.0015,
  expectancyMode: {
    value: 'median',
    view: 'list',
    label: 'ExpectancyMode',
    options: [
      { text: 'avg', value: 'avg' },
      { text: 'median', value: 'median' }
    ],
  },
  expectancyTakeShift: 0,
  expectancyStopMultiplier: 1,
  // 
  waveWindowOptimizer: {
    volatility: 3.2,
    duration: 1.4,
    minPercent: 0.005,
  },
}
export const BacktestSettings = { ...BacktestSettingsDefault }


export const indicatorsConfig = [
  {
    keyPrefix: 'ATR',
    indicator: ATR,
    variants: [3, 5, 9, 14, 21, 42, 64, 89, 144, 256],
    argsValue: ['high', 'low', 'close'],
    argsMoment: ['high', 'low'],
    resultKey: 'value',
  },
  {
    keyPrefix: 'NATR',
    indicator: NATR,
    variants: [3, 5, 9, 14, 21, 42, 64, 89, 144, 256],
    argsValue: ['high', 'low', 'close'],
    argsMoment: ['high', 'low'],
    resultKey: 'value',
  },
  {
    keyPrefix: 'EMA',
    indicator: EMA,
    variants: [3, 5, 9, 14, 21, 42, 64, 89, 144],
    argsValue: ['avg4'],
    argsMoment: ['avg4'],
    resultKey: 'value',
  },
  {
    keyPrefix: 'VolEMA',
    indicator: EMA,
    variants: [3, 5, 9, 14, 21, 42, 64, 89, 144],
    argsValue: ['volume'],
    argsMoment: ['volume'],
    resultKey: 'value',
  },
  {
    keyPrefix: 'SMMA',
    indicator: SMMA,
    variants: [3, 5, 9, 14, 21, 42, 64, 89, 144],
    argsValue: ['avg4'],
    argsMoment: ['avg4'],
    resultKey: 'value',
  },
  {
    keyPrefix: 'AO',
    indicator: AO,
    variants: [[3, 21], [5, 34], [9, 42], [14, 64], [21, 89], [42, 144]],
    argsValue: ['high', 'low'],
    argsMoment: ['high', 'low'],
    resultKey: 'value',
  },
  {
    keyPrefix: 'AC',
    indicator: AC,
    variants: [undefined],
    argsValue: ['high', 'low'],
    argsMoment: ['high', 'low'],
    resultKey: 'value',
  },
  {
    keyPrefix: 'Stochastic',
    indicator: Stochastic,
    variants: [[3, 21], [5, 34], [9, 42], [14, 64], [21, 89], [42, 144], [64, 256]],
    argsValue: ['high', 'low', 'close'],
    argsMoment: ['high', 'low', 'close'],
    resultKey: 'value',
    resultSubKeys: ['k', 'd'],
  },
  {
    keyPrefix: 'StochasticRSI',
    indicator: StochasticRSI,
    variants: [[3, 3, 14, 14]],
    argsValue: ['close'],
    argsMoment: ['close'],
    resultKey: 'value',
    resultSubKeys: ['k', 'd', 'stochRsi'],
  },
  {
    keyPrefix: 'RSI',
    indicator: RSI,
    variants: [3, 5, 9, 14, 21, 42, 64, 89, 144],
    argsValue: ['close'],
    argsMoment: ['close'],
    resultKey: 'value',
  },
  {
    keyPrefix: 'CCI',
    indicator: CCI,
    variants: [3, 5, 9, 14, 21, 42, 64, 89, 144],
    argsValue: ['high', 'low', 'close'],
    argsMoment: ['high', 'low', 'close'],
    resultKey: 'value',
  },
  {
    keyPrefix: 'ROC',
    indicator: ROC,
    variants: [3, 5, 9, 14, 21, 42, 64, 89, 144],
    argsValue: ['close'],
    argsMoment: ['close'],
    resultKey: 'value',
  },
  {
    keyPrefix: 'DC',
    indicator: DC,
    variants: [3, 5, 9, 14, 21, 42, 64, 89, 144],
    argsValue: ['high', 'low'],
    argsMoment: ['high', 'low'],
    resultKey: 'value',
    resultSubKeys: ['upper', 'middle', 'lower'],
  },
  {
    keyPrefix: 'cRSI',
    indicator: cRSI,
    variants: [[3, 2, 100]],
    argsValue: ['close'],
    argsMoment: ['close'],
    resultKey: 'value',
  },
  {
    keyPrefix: 'ADX',
    indicator: ADX,
    variants: [3, 5, 9, 14, 21, 42, 89, 144],
    argsValue: ['high', 'low', 'close'],
    argsMoment: ['high', 'low', 'close'],
    resultKey: 'value',
    resultSubKeys: ['adx', 'pdi', 'mdi'],
  },
  {
    keyPrefix: 'ChaikinOscillator',
    indicator: ChaikinOscillator,
    variants: [[2, 7], [3, 10], [7, 42], [14, 89]],
    argsValue: ['high', 'low', 'close', 'volume'],
    argsMoment: ['high', 'low', 'close', 'volume'],
    resultKey: 'value',
  },
  {
    keyPrefix: 'ZeroLagChaikinOscillator',
    indicator: ZeroLagChaikinOscillator,
    variants: [[3, 10], [44, 111], [66, 155]],
    argsValue: ['high', 'low', 'close', 'volume'],
    argsMoment: ['high', 'low', 'close', 'volume'],
    resultKey: 'value',
  },
  {
    keyPrefix: 'PSAR',
    indicator: PSAR,
    variants: [[0.002, 0.005, 0.2], [0.002, 0.007, 0.2], [0.002, 0.01, 0.2]],
    argsValue: ['high', 'low', 'close'],
    argsMoment: ['high', 'low', 'close'],
    resultKey: 'value',
  },
]

export const indicatorsList = []
indicatorsConfig.forEach(config => {
  const { keyPrefix, indicator, variants, argsValue, argsMoment, resultKey, resultSubKeys } = config
  variants.forEach(variant => {
    const isSingle = !Array.isArray(variant)
    const postfix = isSingle ? variant || 0 : variant.join('_')
    const opts = {
      group: keyPrefix,
      key: `${keyPrefix}_${postfix}`,
      indicator,
      indicatorOpts: isSingle ? [variant] : variant,
      argsValue,
      argsMoment,
      resultKey,
      resultSubKeys,
    }
    indicatorsList.push(opts)
  })
})

// console.log('indicatorsList', indicatorsList)
// window.indicatorsList = indicatorsList

export const strategiesConfig = [
  // === Группа 1: Стратегии КОНТР-ТРЕНД (Fade the Move) ===
  {
    id: 'fade_move',
    title: "Fade: Move",
    strategyType: 'fadeMove',
    side: 'any',
    depth: 0,
    setupWaveType: 'move',
    setupWaveDirection: 'any',
    prevWaveType: 'any',
    setupWavePath: 'move',
    smallWinOutcomePath: 'sequences.pullbackPostMove',
    bigWinOutcomePath: 'sequences.movePostMove',
  },
  {
    id: 'fade_move_up',
    title: "Fade: Move Up",
    strategyType: 'fadeMove',
    side: 'short',
    depth: 1,
    setupWaveType: 'move',
    setupWaveDirection: 'up',
    prevWaveType: 'any',
    setupWavePath: 'move.up',
    smallWinOutcomePath: 'sequences.pullbackPostMove.down',
    bigWinOutcomePath: 'sequences.movePostMove.down',
  },
  {
    id: 'fade_move_down',
    title: "Fade: Move Down",
    strategyType: 'fadeMove',
    side: 'long',
    depth: 1,
    setupWaveType: 'move',
    setupWaveDirection: 'down',
    prevWaveType: 'any',
    setupWavePath: 'move.down',
    smallWinOutcomePath: 'sequences.pullbackPostMove.up',
    bigWinOutcomePath: 'sequences.movePostMove.up',
  },
  {
    id: 'fade_movePostPullback',
    title: "Fade: Move after Pullback",
    strategyType: 'fadeMove',
    side: 'any',
    depth: 1,
    setupWaveType: 'move',
    setupWaveDirection: 'any',
    prevWaveType: 'pullback',
    setupWavePath: 'sequences.movePostPullback',
    smallWinOutcomePath: 'sequences.pullbackPostMove',
    bigWinOutcomePath: 'sequences.movePostMove',
  },
  {
    id: 'fade_movePostPullbackUp',
    title: "Fade: Move Up after Pullback",
    strategyType: 'fadeMove',
    side: 'short',
    depth: 2,
    setupWaveType: 'move',
    setupWaveDirection: 'up',
    prevWaveType: 'pullback',
    setupWavePath: 'sequences.movePostPullback.up',
    smallWinOutcomePath: 'sequences.pullbackPostMove.down',
    bigWinOutcomePath: 'sequences.movePostMove.down',
  },
  {
    id: 'fade_movePostPullbackDown',
    title: "Fade: Move Down after Pullback",
    strategyType: 'fadeMove',
    side: 'long',
    depth: 2,
    setupWaveType: 'move',
    setupWaveDirection: 'down',
    prevWaveType: 'pullback',
    setupWavePath: 'sequences.movePostPullback.down',
    smallWinOutcomePath: 'sequences.pullbackPostMove.up',
    bigWinOutcomePath: 'sequences.movePostMove.up',
  },
  {
    id: 'fade_movePostMove',
    title: "Fade: Move after Move",
    strategyType: 'fadeMove',
    side: 'any',
    depth: 1,
    setupWaveType: 'move',
    setupWaveDirection: 'any',
    prevWaveType: 'move',
    setupWavePath: 'sequences.movePostMove',
    smallWinOutcomePath: 'sequences.pullbackPostMove',
    bigWinOutcomePath: 'sequences.movePostMove',
  },
  {
    id: 'fade_movePostMoveUp',
    title: "Fade: Move Up after Move",
    strategyType: 'fadeMove',
    side: 'short',
    depth: 2,
    setupWaveType: 'move',
    setupWaveDirection: 'up',
    prevWaveType: 'move',
    setupWavePath: 'sequences.movePostMove.up',
    smallWinOutcomePath: 'sequences.pullbackPostMove.down',
    bigWinOutcomePath: 'sequences.movePostMove.down',
  },
  {
    id: 'fade_movePostMoveDown',
    title: "Fade: Move Down after Move",
    strategyType: 'fadeMove',
    side: 'long',
    depth: 2,
    setupWaveType: 'move',
    setupWaveDirection: 'down',
    prevWaveType: 'move',
    setupWavePath: 'sequences.movePostMove.down',
    smallWinOutcomePath: 'sequences.pullbackPostMove.up',
    bigWinOutcomePath: 'sequences.movePostMove.up',
  },

  // === Группа 2: Стратегии ПО ТРЕНДУ (Continue on Pullback) ===
  {
    id: 'continue_pullback',
    title: "Continue: Pullback",
    strategyType: 'continuePullback',
    side: 'any',
    depth: 0,
    setupWaveType: 'pullback',
    setupWaveDirection: 'any',
    prevWaveType: 'any',
    setupWavePath: 'pullback',
    smallWinOutcomePath: 'sequences.pullbackPostPullback',
    bigWinOutcomePath: 'sequences.movePostPullback',
  },
  {
    id: 'continue_pullback_up',
    title: "Continue: Pullback Up",
    strategyType: 'continuePullback',
    side: 'short',
    depth: 1,
    setupWaveType: 'pullback',
    setupWaveDirection: 'up',
    prevWaveType: 'any',
    setupWavePath: 'pullback.up',
    smallWinOutcomePath: 'sequences.pullbackPostPullback.down',
    bigWinOutcomePath: 'sequences.movePostPullback.down',
  },
  {
    id: 'continue_pullback_down',
    title: "Continue: Pullback Down",
    strategyType: 'continuePullback',
    side: 'long',
    depth: 1,
    setupWaveType: 'pullback',
    setupWaveDirection: 'down',
    prevWaveType: 'any',
    setupWavePath: 'pullback.down',
    smallWinOutcomePath: 'sequences.pullbackPostPullback.up',
    bigWinOutcomePath: 'sequences.movePostPullback.up',
  },
  {
    id: 'continue_pullbackPostMove',
    title: "Continue: Pullback after Move",
    strategyType: 'continuePullback',
    side: 'any',
    depth: 1,
    setupWaveType: 'pullback',
    setupWaveDirection: 'any',
    prevWaveType: 'move',
    setupWavePath: 'sequences.pullbackPostMove',
    smallWinOutcomePath: 'sequences.pullbackPostPullback',
    bigWinOutcomePath: 'sequences.movePostPullback',
  },
  {
    id: 'continue_pullbackPostMoveUp',
    title: "Continue: Pullback Up after Move",
    strategyType: 'continuePullback',
    side: 'short',
    depth: 2,
    setupWaveType: 'pullback',
    setupWaveDirection: 'up',
    prevWaveType: 'move',
    setupWavePath: 'sequences.pullbackPostMove.up',
    smallWinOutcomePath: 'sequences.pullbackPostPullback.down',
    bigWinOutcomePath: 'sequences.movePostPullback.down',
  },
  {
    id: 'continue_pullbackPostMoveDown',
    title: "Continue: Pullback Down after Move",
    strategyType: 'continuePullback',
    setupWaveType: 'pullback',
    side: 'long',
    depth: 2,
    setupWaveDirection: 'down',
    prevWaveType: 'move',
    setupWavePath: 'sequences.pullbackPostMove.down',
    smallWinOutcomePath: 'sequences.pullbackPostPullback.up',
    bigWinOutcomePath: 'sequences.movePostPullback.up',
  },
  {
    id: 'continue_pullbackPostPullback',
    title: "Continue: Pullback after Pullback",
    strategyType: 'continuePullback',
    side: 'any',
    depth: 1,
    setupWaveType: 'pullback',
    setupWaveDirection: 'any',
    prevWaveType: 'pullback',
    setupWavePath: 'sequences.pullbackPostPullback',
    smallWinOutcomePath: 'sequences.pullbackPostPullback',
    bigWinOutcomePath: 'sequences.movePostPullback',
  },
  {
    id: 'continue_pullbackPostPullbackUp',
    title: "Continue: Pullback Up after Pullback",
    strategyType: 'continuePullback',
    side: 'short',
    depth: 2,
    setupWaveType: 'pullback',
    setupWaveDirection: 'up',
    prevWaveType: 'pullback',
    setupWavePath: 'sequences.pullbackPostPullback.up',
    smallWinOutcomePath: 'sequences.pullbackPostPullback.down',
    bigWinOutcomePath: 'sequences.movePostPullback.down',
  },
  {
    id: 'continue_pullbackPostPullbackDown',
    title: "Continue: Pullback Down after Pullback",
    strategyType: 'continuePullback',
    side: 'long',
    depth: 2,
    setupWaveType: 'pullback',
    setupWaveDirection: 'down',
    prevWaveType: 'pullback',
    setupWavePath: 'sequences.pullbackPostPullback.down',
    smallWinOutcomePath: 'sequences.pullbackPostPullback.up',
    bigWinOutcomePath: 'sequences.movePostPullback.up',
  },
];